import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "@/core/components/dv-forms/select";
import Input from "@/core/components/dv-forms/input";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { BalanceService, WithdrawRequest } from "@/core/services/balanceService";
import { useTranslation } from "react-i18next";
import { MasterAccountService } from "@/core/services/masterAccountService";
import Loading from "@/core/components/globals/loading";
import { AllocationRepartition } from "@/types/main-account";
import TravelRuleModal from "@/core/components/modal/travel-rule";
import WithdrawOTPModal from "@/core/components/modal/withdraw-otp";

const NETWORK_OPTIONS = [
  { content: 'BSC', value: 'BSC' },
  { content: 'BTC', value: 'BTC' },
  { content: 'SOL', value: 'SOL' },
  { content: 'ETH', value: 'ETH' },
  { content: 'MATIC', value: 'MATIC' },
  { content: 'BASE', value: 'BASE' },
  { content: 'ARBITRUM', value: 'ARBITRUM' },
  { content: 'OPTIMISM', value: 'OPTIMISM' },
  { content: 'SUI', value: 'SUI' },
];

enum AMOUNT_TYPE {
  ASSET = 'asset',
  BALANCE = 'balance'
}

const WithdrawForm = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const { register, handleSubmit, control, formState: { errors, isValid }, watch, setValue, getValues } = useForm({
    mode: 'onChange',
    defaultValues: {
      coin: '',
      network: '',
      address: '',
      amount: 0,
      amountType: AMOUNT_TYPE.ASSET
    }
  });

  // Data stored before OTP verification (without code)
  interface WithdrawFormData {
    coin: string;
    network: string;
    address: string;
    amount: number;
  }

  const [assets, setAssets] = useState<AllocationRepartition[]>([]);
  const [assetOptions, setAssetOptions] = useState<any[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<AllocationRepartition>();
  const [showTravelRuleModal, setShowTravelRuleModal] = useState<boolean>(false);
  const [showOTPModal, setShowOTPModal] = useState<boolean>(false);
  const [withdrawalData, setWithdrawalData] = useState<WithdrawFormData | null>(null);

  const coin = watch('coin');
  const amountType = watch('amountType');

  // Fetch master account allocation
  const { isLoading: isLoadingAssets } = useQuery({
    queryKey: ['masterAccountAllocation'],
    refetchOnMount: 'always',
    queryFn: () => MasterAccountService.getMainAccountAllocation(),
    onSuccess: (data) => {
      if (data && data.length > 0) {
        setAssets(data);
        const options = data.map((allocation: AllocationRepartition) => ({
          content: `${allocation.asset} (${allocation.amount} ${allocation.asset})`,
          value: allocation.asset,
          asset: allocation
        }));
        setAssetOptions(options);
      }
    }
  });

  // Handle withdraw submission
  const { mutate: withdrawFunds, isLoading: isWithdrawing } = useMutation({
    mutationFn: (data: WithdrawRequest) => BalanceService.withdraw(data),
    onSuccess: (response) => {
      if (response.status === "success") {
        toast.success(t("message.withdraw_success", { amount: getValues('amount'), coin: getValues('coin') }));
        onClose();
      } else if (response.status === "pending_travel_rule") {
        setShowTravelRuleModal(true);
      }
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t("message.withdraw_error"));
    }
  });

  // Handle form submission
  const onSubmit = (values: any) => {
    let finalAmount = Number(values.amount);

    // Convert balance to asset amount if needed
    if (values.amountType === AMOUNT_TYPE.BALANCE && selectedAsset) {
      // Use the marketPrice for conversion
      finalAmount = Number(values.amount) / selectedAsset.marketPrice;
    }

    // Store withdrawal data and show OTP modal
    const data = {
      coin: values.coin,
      network: values.network,
      address: values.address,
      amount: finalAmount
    };

    setWithdrawalData(data);
    setShowOTPModal(true);
  };

  // Handle successful OTP verification
  const handleOTPSuccess = (code: string) => {
    if (withdrawalData) {
      withdrawFunds({ ...withdrawalData, code });
    }
    setShowOTPModal(false);
  };

  // Set max amount
  const handleSetMaxAmount = () => {
    if (selectedAsset) {
      if (amountType === AMOUNT_TYPE.ASSET) {
        setValue('amount', selectedAsset.amount);
      } else {
        // Use the in_usdc value directly
        setValue('amount', selectedAsset.in_usdc);
      }
    }
  };

  // Update selected asset when coin changes
  useEffect(() => {
    const asset = assets.find((a: AllocationRepartition) => a.asset === coin);
    setSelectedAsset(asset);
  }, [coin, assets]);

  return (
    <>
      {showTravelRuleModal ? (
        <TravelRuleModal onClose={() => setShowTravelRuleModal(false)} isFullModal={false} />
      ) : showOTPModal ? (
        <WithdrawOTPModal
          onClose={() => {
            setShowOTPModal(false);
            onClose();
          }}
          onSuccess={handleOTPSuccess}
        />
      ) : (
        <>
          <div className="general-modal__content" style={{ overflow: 'unset' }}>
            <div>
              {isLoadingAssets ? (
                <Loading />
              ) : assets.length === 0 ? (
                <div className="no-assets-message">
                  <p>{t("common.no_assets_in_master_account")}</p>
                </div>
              ) : (
                <form className="dv-form">
                  <Controller
                    name="coin"
                    control={control}
                    rules={{ required: t("form.required.asset") }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        closeEnable
                        label={t("common.asset")}
                        placeholder={t("common.select_asset")}
                        options={assetOptions}
                        errors={errors}
                      />
                    )}
                  />

                  <Controller
                    name="network"
                    control={control}
                    rules={{ required: t("form.required.network") }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        closeEnable
                        label={t("common.network")}
                        placeholder={t("common.select_network")}
                        options={NETWORK_OPTIONS}
                        errors={errors}
                      />
                    )}
                  />

                  <Controller
                    name="amountType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        closeEnable
                        label={t("common.amount_type")}
                        placeholder={t("common.select_amount_type")}
                        options={[
                          { content: t('common.asset_amount') + (coin ? ' (' + coin + ')' : ''), value: AMOUNT_TYPE.ASSET },
                          { content: t('common.balance_usdc'), value: AMOUNT_TYPE.BALANCE }
                        ]}
                        errors={errors}
                        value={AMOUNT_TYPE.BALANCE}
                      />
                    )}
                  />

                  <Input
                    name="amount"
                    label={t("common.amount")}
                    placeholder="0"
                    register={register}
                    errors={errors}
                    startAdornment={amountType === AMOUNT_TYPE.BALANCE ? '$' : ''}
                    suffix={amountType === AMOUNT_TYPE.ASSET && coin ? coin : ''}
                    type="number"
                    rules={{
                      required: t("form.required.amount"),
                      min: { value: 0.00000001, message: t("form.invalid.min_amount") },
                      validate: (value) => {
                        if (!selectedAsset) return true;
                        if (amountType === AMOUNT_TYPE.ASSET && Number(value) > selectedAsset.amount) {
                          return t("form.invalid.insufficient_funds");
                        }
                        if (amountType === AMOUNT_TYPE.BALANCE && Number(value) > selectedAsset.in_usdc) {
                          return t("form.invalid.insufficient_funds");
                        }
                        return true;
                      }
                    }}
                    endAdornment={
                      <button
                        type="button"
                        className="max-button"
                        onClick={handleSetMaxAmount}
                        disabled={!selectedAsset}
                      >
                        {t("common.max")}
                      </button>
                    }
                  />

                  <Input
                    name="address"
                    label={t("common.address")}
                    placeholder={t("common.enter_address")}
                    register={register}
                    errors={errors}
                    rules={{ required: t("form.required.address") }}
                  />
                </form>
              )}
            </div>
          </div>
          <div className="general-modal__footer">
            {assets.length === 0 ? (
              <button
                className="cta-button cta-large cta-blue"
                onClick={onClose}
              >
                <span>{toTitleCase(t('common.close'))}</span>
              </button>
            ) : (
              <button
                disabled={!isValid || isWithdrawing}
                className="cta-button cta-large cta-blue"
                onClick={handleSubmit(onSubmit)}
              >
                <span>{toTitleCase(
                  t(isWithdrawing ? 'common.withdrawing...' : 'common.confirm')
                )}</span>
              </button>
            )}
          </div>
        </>
      )}
    </>
  );
};

function toTitleCase(str: string): string {
  return str.replace(
    /\w\S*/g,
    text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
  );
}

export default WithdrawForm;
